import { useState, useEffect, useRef, useCallback } from "react";
import "../CSS/ConsignmentBooking.css";
import useDraggableScroll from "../hooks/useDraggableScroll";
import api from "../api";

// ── External: JsBarcode is loaded via CDN script tag in index.html:
// <script src="https://cdnjs.cloudflare.com/ajax/libs/JsBarcode/3.11.5/JsBarcode.all.min.js"></script>

/* ══════════════════════════════════════════════════
   STATIC FALLBACK DATA
══════════════════════════════════════════════════ */
const PLACE_NAMES = {
  MUM: "Mumbai",    DEL: "Delhi",      BLR: "Bengaluru",
  CHE: "Chennai",   HYD: "Hyderabad",  PUN: "Pune",
  AHM: "Ahmedabad", KOL: "Kolkata",    JKP: "Jaipur",
  LKN: "Lucknow",   NAG: "Nagpur",     SRT: "Surat",
};
const PINCODES = {
  MUM: "400001", DEL: "110001", BLR: "560001",
  CHE: "600001", HYD: "500001", PUN: "411001",
  AHM: "380001", KOL: "700001", JKP: "302001",
  LKN: "226001", NAG: "440001", SRT: "395003",
};
const PARTIES = {
  TML: { name: "Tata Motors Ltd.",      gst: "27AAACT2727Q1ZW", rate: 18, basis: "perkg" },
  MSI: { name: "Maruti Suzuki India",   gst: "08AAACM4685R1Z5", rate: 22, basis: "perkg" },
  BAL: { name: "Bajaj Auto Ltd.",       gst: "27AAACB0534F1Z5", rate: 15, basis: "perkg" },
  BSH: { name: "Bosch India",           gst: "29AAACB4849M1ZX", rate: 25, basis: "perpkg" },
  MNL: { name: "Motherson Neeraj Ltd.", gst: "07AABCM1787R1ZA", rate: 20, basis: "perkg" },
  SVP: { name: "Sundaram Valeo Pvt.",   gst: "33AAACS1049Q1ZX", rate: 17, basis: "perkg" },
};
const PARTY_JOBS = {
  TML: ["Truck Division", "Car Division", "EV Parts", "After-Market", "Exports", "R&D Spares"],
  MSI: ["Passenger Cars", "Commercial", "Export Parts", "Dealer Supply", "OEM Parts"],
  BAL: ["2W Division", "3W Division", "EV Division", "International", "Racing"],
  BSH: ["Automotive", "Industrial", "Aftermarket", "Export"],
  MNL: ["Wire Harness", "Plastic Parts", "Metal Parts", "EV Components"],
  SVP: ["Thermal Systems", "Friction Parts", "Transmission", "Electrical"],
};
const PKG_TYPES = ["Box / Carton", "Wooden Crate", "Pallet", "Drum", "Bag / Sack", "Bundle", "Loose / Naked"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
function fmtDate(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function fmt(n) {
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function calcExpDeliveryDate(priority, bookingDate) {
  const days = { standard: 3, express: 1, urgent: 0 };
  const base = bookingDate ? new Date(bookingDate) : new Date();
  const d = new Date(base);
  d.setDate(d.getDate() + (days[priority] || 3));
  return d.toISOString().split('T')[0];
}
function formatDateDisplay(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}


// /* ══════════════════════════════════════════════════
//    AUTOCOMPLETE COMPONENT (old design classes)
// ══════════════════════════════════════════════════ */
// function AutoComplete({ options, value, onChange, placeholder = "Search…" }) {
//   const [query, setQuery] = useState("");
//   const [open, setOpen] = useState(false);
//   const [focused, setFocused] = useState(-1);
//   const wrapRef = useRef(null);

//   const selected = options.find((o) => o.value === value);

//   useEffect(() => {
//     if (selected) setQuery(selected.label);
//     else setQuery("");
//   }, [value, selected]);

//   const filtered = options.filter(
//     (o) => o.value !== undefined && o.label.toLowerCase().includes((query || "").toLowerCase())
//   );

//   function hl(text, q) {
//     if (!q) return text;
//     const i = text.toLowerCase().indexOf(q.toLowerCase());
//     if (i < 0) return text;
//     return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
//   }

//   function choose(opt) {
//     onChange(opt.value);
//     setQuery(opt.label);
//     setOpen(false);
//     setFocused(-1);
//   }

//   function clear() {
//     onChange("");
//     setQuery("");
//     setOpen(true);
//     setFocused(-1);
//   }

//   useEffect(() => {
//     function handle(e) {
//       if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
//     }
//     document.addEventListener("mousedown", handle);
//     return () => document.removeEventListener("mousedown", handle);
//   }, []);

//   function handleKeyDown(e) {
//     if (!open) { if (e.key === "ArrowDown" || e.key === "Enter") setOpen(true); return; }
//     if (e.key === "ArrowDown") { e.preventDefault(); setFocused((f) => Math.min(f + 1, filtered.length - 1)); }
//     else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((f) => Math.max(f - 1, 0)); }
//     else if (e.key === "Enter") { e.preventDefault(); if (focused >= 0) choose(filtered[focused]); else if (filtered.length === 1) choose(filtered[0]); }
//     else if (e.key === "Escape") setOpen(false);
//   }

//   return (
//     <div className="cb-ac" ref={wrapRef}>
//       <div className={`cb-ac-box${open ? " open" : ""}`}>
//         <input
//           className="cb-ac-in"
//           type="text"
//           autoComplete="off"
//           placeholder={placeholder}
//           value={query}
//           onFocus={() => setOpen(true)}
//           onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
//           onKeyDown={handleKeyDown}
//         />
//         {value && (
//           <span className="cb-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
//         )}
//         <span className="cb-ac-chev" onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}>▾</span>
//       </div>
//       {open && (
//         <div className="cb-ac-drop open">
//           {filtered.length === 0 ? (
//             <div className="cb-ac-empty">No matches found</div>
//           ) : (
//             filtered.map((opt, i) => (
//               <div
//                 key={opt.value}
//                 className={`cb-ac-opt${opt.value === value ? " sel" : ""}${i === focused ? " foc" : ""}`}
//                 onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
//               >
//                 {hl(opt.label, query)}
//               </div>
//             ))
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

// function handleEnterKey(e) {
//   if (e.key !== "Enter") return;
//   e.preventDefault();

//   // Get all focusable inputs/selects in the entire form (in DOM order)
//   const focusable = Array.from(
//     document.querySelectorAll(
//       'input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled])'
//     )
//   );

//   const current = e.target;
//   const idx = focusable.indexOf(current);
//   if (idx !== -1 && idx < focusable.length - 1) {
//     focusable[idx + 1].focus();
//   }
// }



function AutoComplete({ options, value, onChange, placeholder = "Search…", onEnter }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);   // renamed from 'open' to avoid ESLint conflict
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else setQuery("");
  }, [value, selected]);

  //without handle null
  // const filtered = options.filter(
  //   (o) => o.value !== undefined && o.label.toLowerCase().includes((query || "").toLowerCase())
  // );
//with handle null/undefined
  const filtered = options.filter(
  (o) => o.value !== undefined && o.label != null && o.label.toLowerCase().includes((query || "").toLowerCase())
);

  function hl(text, q) {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
  }

  // function choose(opt) {
  //   onChange(opt.value);
  //   setQuery(opt.label);
  //   setIsOpen(false);
  //   setFocused(-1);
  //   setCursorMoved(false);
  //   setTimeout(() => inputRef.current?.blur(), 10);
  // }
function choose(opt) {
  onChange(opt.value);
  setQuery(opt.label);
  setIsOpen(false);
  setFocused(-1);
  setCursorMoved(false);
  // Don't blur — let fireEnter focus next field instead
}

  function clear() {
    onChange("");
    setQuery("");
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // function fireEnter() {
  //   if (onEnter) onEnter({ target: inputRef.current, preventDefault: () => {} });
  // }

function fireEnter() {
  const focusable = Array.from(
    document.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]):not([type="checkbox"]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
    )
  ).filter(el => el.offsetParent !== null);
  const idx = focusable.indexOf(inputRef.current);
  if (idx !== -1 && idx < focusable.length - 1) {
    focusable[idx + 1].focus();
  }
}
  function handleKeyDown(e) {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        setCursorMoved(true);
      } else if (e.key === "Enter") {
        fireEnter();  // dropdown closed → go next field
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (cursorMoved && filtered.length > 0) {
        const target = filtered[focused] || filtered[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
      setTimeout(fireEnter, 20);  // go next field after selecting
    }
  }

  return (
    <div className="cb-ac" ref={wrapRef}>
      <div className={`cb-ac-box${isOpen ? " open" : ""}`}>
        <input
          ref={inputRef}
          className="cb-ac-in"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onFocus={() => { setIsOpen(true); setCursorMoved(false); }}
          onClick={() => { if (!isOpen) setIsOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <span className="cb-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        <span className="cb-ac-chev" onMouseDown={(e) => { e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
      </div>
      {isOpen && (
        <div className="cb-ac-drop open">
          {filtered.length === 0 ? (
            <div className="cb-ac-empty">No matches found</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                className={`cb-ac-opt${opt.value === value ? " sel" : ""}${i === focused ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                onMouseEnter={() => setFocused(i)}
              >
                {hl(opt.label, query)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}




// ── Enter key → next field for plain inputs ──

/* ══════════════════════════════════════════════════
   TOGGLE SWITCH
══════════════════════════════════════════════════ */
function Toggle({ checked, onChange }) {
  return (
    <label className="cb-sw">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="cb-sw-track" />
    </label>
  );
}

/* ══════════════════════════════════════════════════
   TOAST SYSTEM
══════════════════════════════════════════════════ */
function ToastContainer({ toasts }) {
  return (
    <div className="cb-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`cb-toast t-${t.type}`}>
          <span style={{ fontSize: 14 }}>{t.type === "ok" ? "✓" : t.type === "err" ? "✕" : t.type === "warn" ? "⚠" : "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   PIECE ROW
══════════════════════════════════════════════════ */
function PieceRow({ idx, row, onChange, onDelete }) {
  const vol = ((row.l * row.w * row.h) / 5000) * row.qty;
  const act = row.actW * row.qty;
  const chg = Math.max(act, vol);
  return (
    <tr>
      <td>{idx + 1}</td>
      <td><input type="text" value={row.desc} onChange={(e) => onChange({ ...row, desc: e.target.value })} style={{ minWidth: 120 }} /></td>
      <td>
        <select value={row.pkgType} onChange={(e) => onChange({ ...row, pkgType: e.target.value })}>
          {PKG_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </td>
      <td><input type="number" value={row.qty} min={1} onChange={(e) => onChange({ ...row, qty: parseFloat(e.target.value) || 0 })} style={{ width: 55 }} /></td>
      <td><input type="number" value={row.l} min={0} onChange={(e) => onChange({ ...row, l: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input type="number" value={row.w} min={0} onChange={(e) => onChange({ ...row, w: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input type="number" value={row.h} min={0} onChange={(e) => onChange({ ...row, h: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input type="number" value={row.actW} min={0} step={0.1} onChange={(e) => onChange({ ...row, actW: parseFloat(e.target.value) || 0 })} style={{ width: 76 }} /></td>
      <td className="cb-vol-cell">{vol > 0 ? vol.toFixed(2) + " kg" : "—"}</td>
      <td className="cb-chg-cell">{chg > 0 ? chg.toFixed(2) + " kg" : "—"}</td>
      <td><button className="cb-del-btn" onClick={onDelete}>✕</button></td>
    </tr>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function ConsignmentBooking() {
  const today = new Date();
  const todayStr = fmtDate(today);

  /* ── Consignment No ── */
  const [cnr, setCnr] = useState("");

  /* ── Step ── */
  const [step, setStep] = useState(1);
  const [bookingDate, setBookingDate] = useState(() => new Date().toISOString().split('T')[0]);

  /* ── Route ── */
  const [bookingPlace, setBookingPlace] = useState("");
  const [bookingPin, setBookingPin] = useState("");
  const [bpinInfo, setBpinInfo] = useState("");
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [deliveryPin, setDeliveryPin] = useState("");
  const [dpinInfo, setDpinInfo] = useState("");
  const [priority, setPriority] = useState("");
  const [expDelivery, setExpDelivery] = useState("");

  /* ── Party ── */
  const [partyKey, setPartyKey] = useState("");
  const [partyGst, setPartyGst] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [freight, setFreight] = useState("");
  const [partyJobs, setPartyJobs] = useState([]);

  /* ── Consignor ── */
  const emptyParty = { name: "", contact: "", mobile: "", addr: "", gst: "", pin: "" };
  const [cnorSel, setCnorSel] = useState("");
  const [cnorData, setCnorData] = useState(emptyParty);
  const [cnorMode, setCnorMode] = useState("list");

  /* ── Consignee ── */
  const [cneeSel, setCneeSel] = useState("");
  const [cneeData, setCneeData] = useState(emptyParty);
  const [cneeMode, setCneeMode] = useState("list");

  /* ── OTP ── */
  const [otpOn, setOtpOn] = useState(false);
  const [otpMobile, setOtpMobile] = useState("");
  const [otpChannel, setOtpChannel] = useState("both");
  const [otpValidity, setOtpValidity] = useState("30");
  const [otpAttempts, setOtpAttempts] = useState("3");

  /* ── Notifications ── */
  const [notifMaster, setNotifMaster] = useState(false);
  const [smsOn, setSmsOn] = useState(true);
  const [emailOn, setEmailOn] = useState(true);
  const [waOn, setWaOn] = useState(false);
  const [smsNum, setSmsNum] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [waNum, setWaNum] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [notificationEvents, setNotificationEvents] = useState([]);
  const [events, setEvents] = useState([]);

  /* ── Pieces ── */

    const [isVolumetric, setIsVolumetric] = useState(false);
  const [manualPcs, setManualPcs] = useState("");
  const [manualChgWt, setManualChgWt] = useState("");
  const [pieces, setPieces] = useState([]);
  const [pidCtr, setPidCtr] = useState(1);
  const [commodity, setCommodity] = useState("");
  const [declVal, setDeclVal] = useState("");
  const [specialInst, setSpecialInst] = useState("");

  /* ── Rate ── */
  const [rateBasis, setRateBasis] = useState("perkg");
  const [baseRate, setBaseRate] = useState(0);
  const [fuelPct, setFuelPct] = useState(12);
  const [gstPct, setGstPct] = useState("5");
  const [insAmt, setInsAmt] = useState(0);
  const [otherAmt, setOtherAmt] = useState(0);

  /* ── Fetched Rate Details (from API) ── */
  const [fetchedRateHeader, setFetchedRateHeader] = useState(null);
  const [fetchedRateDetails, setFetchedRateDetails] = useState([]);
  const [rateLoading, setRateLoading] = useState(false);

  /* ── Images ── */
  const [images, setImages] = useState([]);
  const [imgType, setImgType] = useState("Package");

  /* ── API-driven dropdown options ── */
  const [bookingPlaceOpts, setBookingPlaceOpts] = useState([]);
  const [deliveryPlaceOpts, setDeliveryPlaceOpts] = useState([]);
  const [partyOpts, setPartyOpts] = useState([]);
  const [cnorOpts, setCnorOpts] = useState([]);
  const [cneeOpts, setCneeOpts] = useState([]);
  const [allPartiesData, setAllPartiesData] = useState({});
  const [freightTermOpts, setFreightTermOpts] = useState([]);
  const [priorityOpts, setPriorityOpts] = useState([]);

  /* ── Modals ── */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const [stickerData, setStickerData] = useState({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [bookedCnr, setBookedCnr] = useState("");
  const [availableConsignments, setAvailableConsignments] = useState([]);
  const [cnrLoading, setCnrLoading] = useState(false);
  const [rawConsNo, setRawConsNo] = useState(null); // pure numeric ID for payload
  const [rawPrefix, setRawPrefix] = useState("");   // prefix for payload

  /* ── Toast & booking state ── */
  const [toasts, setToasts] = useState([]);
  const [isBooking, setIsBooking] = useState(false);
  const barcodeSvgRef = useRef(null);
  const stepperRef = useDraggableScroll();



  
  /* ══════════════════════════════════════════════════
     GLOBAL ENTER-KEY → NEXT FIELD NAVIGATION
     Works for: input (text/number/date/tel), select, textarea
     Textarea: Enter moves to next field, Shift+Enter inserts newline
  ══════════════════════════════════════════════════ */
  function handleEnterKey(e) {
    if (e.key !== "Enter") return;

    const tag = e.target.tagName.toLowerCase();
    const type = (e.target.type || "").toLowerCase();

    // Skip buttons — let them do their normal action
    if (tag === "button") return;

    // Skip checkboxes — Enter should toggle them normally
    if (type === "checkbox" || type === "radio") return;

    // Skip if inside AutoComplete dropdown (it handles its own Enter)
    const acWrap = e.target.closest(".cb-ac");
    if (acWrap) {
      const dropdown = acWrap.querySelector(".cb-ac-drop.open");
      if (dropdown) return; // let AutoComplete handleKeyDown manage it
    }

    // Textarea: Shift+Enter = newline, plain Enter = next field
    if (tag === "textarea" && e.shiftKey) return;

    e.preventDefault();

    // Build list of all visible focusable fields (same order as DOM)
    const focusable = Array.from(
      document.querySelectorAll(
        'input:not([disabled]):not([readonly]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), '
        + 'select:not([disabled]), '
        + 'textarea:not([disabled]):not([readonly])'
      )
    ).filter(el => el.offsetParent !== null); // only visible elements

    const idx = focusable.indexOf(e.target);
    if (idx !== -1 && idx < focusable.length - 1) {
      focusable[idx + 1].focus();
    }
  }
  /* ══════════════════════════════════════════════════
     TOAST
  ══════════════════════════════════════════════════ */
  function addToast(msg, type = "info") {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }

  


  /* ══════════════════════════════════════════════════
     NOTIFICATION HELPERS
  ══════════════════════════════════════════════════ */
  const syncPartyNotifications = useCallback((partyId) => {
    if (!partyId) return;
    const d = allPartiesData[partyId];
    if (d) {
      setNotifMaster(!!(d.notifSms || d.notifEmail || d.notifWa));
      setSmsOn(d.notifSms);
      setEmailOn(d.notifEmail);
      setWaOn(d.notifWa);
      setSmsNum(d.smsNumber);
      setEmailAddr(d.emailAddress);
      setWaNum(d.waNumber);
      setSelectedEvents(d.selectedEvents || []);
    }
  }, [allPartiesData]);

  const getTitle = (id, fallback) => {
    const ev = events?.find(e => (e.event_id || e.Event_id || e.id || e.Id) === id);
    return ev ? (ev.eventName || ev.EventName || ev.name || ev.Name || ev.event || ev.Event || fallback) : fallback;
  };

  const toggleEvent = (eventId) => {
    setSelectedEvents(prev =>
      prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
    );
  };

  /* ══════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════ */
  useEffect(() => {
    fetchPlaces();
    fetchParties();
    fetchAllParties();
    fetchNotificationEvents();
    fetchEvents();
    fetchFreightTerms();
    fetchPriorities();
    fetchAvailableConsignments();
    setExpDelivery(calcExpDeliveryDate("standard", bookingDate));
    setPieces([
      { id: 1, desc: "Engine Parts", pkgType: "Box / Carton", qty: 2, l: 60, w: 40, h: 35, actW: 12.5 },
      { id: 2, desc: "Brake Pads",   pkgType: "Box / Carton", qty: 1, l: 30, w: 20, h: 15, actW: 3.0 },
    ]);
    setPidCtr(3);
    // eslint-disable-next-line
  }, []);

  const fetchAvailableConsignments = async () => {
    setCnrLoading(true);
    try {
      const res = await api.get('/get-available-consignments');
      const item = res.data;
      const consNo = item?.Consignment_No ?? item?.consignment_No ?? item?.consignmentNo ?? null;
      const prefix  = item?.prefix ?? item?.Prefix ?? "";
      if (consNo != null) {
        const display = prefix ? `${prefix}-${consNo}` : String(consNo);
        setCnr(display);
        setRawConsNo(consNo);
        setRawPrefix(prefix);          // store prefix separately for payload
        setAvailableConsignments([item]);
      } else {
        setCnr("");
        setRawConsNo(null);
        setRawPrefix("");
        setAvailableConsignments([]);
      }
    } catch (err) {
      console.error("Failed to fetch available consignments:", err);
      addToast("Failed to load consignment number", "err");
    } finally {
      setCnrLoading(false);
    }
  };

  const fetchPlaces = async () => {
    try {
      const bookRes = await api.get('/get-booking-places');
      setBookingPlaceOpts(bookRes.data.map(b => ({
        value: b.place_Id || b.Place_Id,
        label: b.place_name || b.Place_name,
        pincode: b.pincode || b.Pincode
      })));
      const delRes = await api.get('/get-delivery-places');
      setDeliveryPlaceOpts(delRes.data.map(b => ({
        value: b.place_Id || b.Place_Id,
        label: b.place_name || b.Place_name,
        pincode: b.pincode || b.Pincode
      })));
    } catch (err) {
      console.error("Failed to fetch places:", err);
      addToast("Failed to fetch places", "err");
    }
  };

  const fetchParties = async () => {
    try {
      const res = await api.get('/get-partylist');
      setPartyOpts(res.data.map(p => ({
        value: p.party_id || p.Party_id,
        label: p.party_Name || p.Party_Name
      })));
    } catch (err) { console.error("Failed to fetch parties:", err); }
  };

  const fetchNotificationEvents = async () => {
    try {
      const res = await api.get('/get-standard-notifications');
      setNotificationEvents(res.data);
    } catch (err) { console.error("Failed to fetch notifications:", err); }
  };

  const fetchFreightTerms = async () => {
    try {
      const res = await api.get('/get-freight-terms');
      setFreightTermOpts(res.data);
    } catch (err) { console.error("Failed to fetch freight terms:", err); }
  };

  const fetchPriorities = async () => {
    try {
      const res = await api.get('/get-priorities');
      setPriorityOpts(res.data);
    } catch (err) { console.error("Failed to fetch priorities:", err); }
  };

  const fetchEvents = async () => {
    try {
      const res = await api.get('/get-events');
      setEvents(res.data);
    } catch (err) { console.error("Failed to fetch events:", err); }
  };

  const fetchAllParties = async () => {
    try {
      const res = await api.get('/get-parties');
      const data = res.data;
      const mapped = data.map(p => ({
        value: p.party_id || p.Party_id,
        label: p.party_Name || p.Party_Name
      }));
      setCnorOpts(mapped);
      setCneeOpts(mapped);
      const lookup = {};
      data.forEach(p => {
        const id = p.party_id || p.Party_id;
        lookup[id] = {
          name: p.party_Name || p.Party_Name || "",
          contact: p.contact_Person || p.Contact_Person || "",
          mobile: p.mobile_No || p.Mobile_No || "",
          addr: p.address || "",
          gst: p.gstin || "",
          pin: p.pincode || p.Pincode || "",
          notifEmail: (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 2) || (p.notification_Preference || p.Notification_Preference) === "E",
          notifSms:   (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 1) || (p.notification_Preference || p.Notification_Preference) === "S",
          notifWa:    (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 3) || (p.notification_Preference || p.Notification_Preference) === "W",
          smsNumber:    p.sms_Number   || p.Sms_Number   || p.mobile_No || p.Mobile_No || "",
          emailAddress: p.party_Email  || p.Party_Email  || p.email_Address || p.Email_Address || "",
          waNumber:     p.wa_Number    || p.Wa_Number    || p.mobile_No || p.Mobile_No || "",
          selectedEvents: (p.notifications || p.Notifications || []).map(n =>
            n.notification_event_id || n.Notification_Event_id || n.Notification_event_id || n.notification_Event_id
          )
        };
      });
      setAllPartiesData(lookup);
    } catch (err) { console.error("Failed to fetch all parties:", err); }
  };

  /* ══════════════════════════════════════════════════
     FETCH RATE DETAILS (from Rate master)
  ══════════════════════════════════════════════════ */
  const fetchRateDetails = useCallback(async (partyId, bookPlaceId, delPlaceId) => {
    if (!partyId || !bookPlaceId || !delPlaceId) {
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
      return;
    }
    setRateLoading(true);
    try {
      const res = await api.get('/get-rate-details', {
        params: {
          partyId: parseInt(partyId),
          bookPlaceId: parseInt(bookPlaceId),
          delPlaceId: parseInt(delPlaceId)
        }
      });
      const { rateHeader, rateDetails } = res.data;
      setFetchedRateHeader(rateHeader || null);
      setFetchedRateDetails(rateDetails || []);
      if (rateHeader) {
        addToast(`Rate card loaded — ${rateDetails?.length || 0} slab(s)`, "ok");
      }
    } catch (err) {
      console.error("Failed to fetch rate details:", err);
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
    } finally {
      setRateLoading(false);
    }
  }, []);

  // Auto-fetch rate details when party, booking place, or delivery place changes
  useEffect(() => {
    if (partyKey && bookingPlace && deliveryPlace) {
      fetchRateDetails(partyKey, bookingPlace, deliveryPlace);
    } else {
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
    }
  }, [partyKey, bookingPlace, deliveryPlace, fetchRateDetails]);

  /**
   * Find the matching rate amount from fetched rate slabs based on weight.
   * Looks for the slab where From_Wgt <= weight <= To_Wgt.
   * Returns { rateAmount, additionalWtRate, uomCode } or null if no match.
   */
  function getSlabRate(weight) {
    if (!fetchedRateDetails || fetchedRateDetails.length === 0) return null;
    const w = parseFloat(weight) || 0;
    // Find matching slab
    const slab = fetchedRateDetails.find(d => {
      const from = parseFloat(d.from_Wgt ?? d.From_Wgt) || 0;
      const to = parseFloat(d.to_Wgt ?? d.To_Wgt) || 0;
      return w >= from && w <= to;
    });
    if (slab) {
      return {
        rateAmount: parseFloat(slab.rateAmount ?? slab.RateAmount) || 0,
        additionalWtRate: parseFloat(slab.addl_Wgt ?? slab.Addl_Wgt) || 0,
        uomCode: slab.uoM_Code ?? slab.UOM_Code ?? "",
        fromWgt: parseFloat(slab.from_Wgt ?? slab.From_Wgt) || 0,
        toWgt: parseFloat(slab.to_Wgt ?? slab.To_Wgt) || 0
      };
    }
    // If weight exceeds all slabs, use the last (highest) slab + additional weight calculation
    const lastSlab = fetchedRateDetails[fetchedRateDetails.length - 1];
    if (lastSlab) {
      const lastTo = parseFloat(lastSlab.to_Wgt ?? lastSlab.To_Wgt) || 0;
      const lastRate = parseFloat(lastSlab.rateAmount ?? lastSlab.RateAmount) || 0;
      const addlRate = parseFloat(lastSlab.addl_Wgt ?? lastSlab.Addl_Wgt) || 0;
      if (w > lastTo && addlRate > 0) {
        const extraWt = w - lastTo;
        return {
          rateAmount: lastRate + (extraWt * addlRate),
          additionalWtRate: addlRate,
          uomCode: lastSlab.uoM_Code ?? lastSlab.UOM_Code ?? "",
          fromWgt: lastTo,
          toWgt: w
        };
      }
    }
    return null;
  }

  /* ══════════════════════════════════════════════════
     ROUTE HANDLERS
  ══════════════════════════════════════════════════ */
  function handlePlaceChange(type, val) {
    if (type === "booking") {
      setBookingPlace(val);
      const opt = bookingPlaceOpts.find(o => String(o.value) === String(val));
      if (opt) {
        setBookingPin(opt.pincode || "");
        setBpinInfo(opt.pincode ? `📍 ${opt.pincode} — ${opt.label}` : `📍 ${opt.label}`);
      } else {
        const pin = PINCODES[val] || "";
        setBookingPin(pin);
        setBpinInfo(pin ? `📍 ${pin} — ${PLACE_NAMES[val] || val}` : "");
      }
    } else {
      setDeliveryPlace(val);
      const opt = deliveryPlaceOpts.find(o => String(o.value) === String(val));
      if (opt) {
        setDeliveryPin(opt.pincode || "");
        setDpinInfo(opt.pincode ? `📍 ${opt.pincode} — ${opt.label}` : `📍 ${opt.label}`);
      } else {
        const pin = PINCODES[val] || "";
        setDeliveryPin(pin);
        setDpinInfo(pin ? `📍 ${pin} — ${PLACE_NAMES[val] || val}` : "");
      }
    }
    setExpDelivery(calcExpDeliveryDate(priority, bookingDate));
  }

  function handlePriorityChange(val) {
    setPriority(val);
    const id = parseInt(val);
    const priorityKey = id === 2 ? "express" : id === 3 ? "urgent" : "standard";
    setExpDelivery(calcExpDeliveryDate(priorityKey, bookingDate));
  }

  function handleDateChange(val) {
    setBookingDate(val);
    const id = parseInt(priority);
    const priorityKey = id === 2 ? "express" : id === 3 ? "urgent" : "standard";
    setExpDelivery(calcExpDeliveryDate(priorityKey, val));
  }

  /* ══════════════════════════════════════════════════
     PARTY HANDLER
  ══════════════════════════════════════════════════ */
  async function handlePartyChange(k) {
    setPartyKey(k);
    setSelectedJob(null);
    setPartyJobs([]);
    if (!k) { setPartyGst(""); setBaseRate(0); return; }
    const p = PARTIES[k];
    if (p) { setPartyGst(p.gst || ""); setBaseRate(p.rate || 0); setRateBasis(p.basis || "perkg"); }
    try {
      const gstRes = await api.get(`/get-party-gstin/${k}`);
      if (gstRes.data && (gstRes.data.success || gstRes.data.GSTIN || gstRes.data.gstin)) {
        setPartyGst(gstRes.data.GSTIN || gstRes.data.gstin || "");
      }
      const jobsRes = await api.get(`/get-job-list/${k}`);
      if (jobsRes.data) setPartyJobs(jobsRes.data);
      syncPartyNotifications(k);
    } catch (err) { console.error("Failed to fetch party details:", err); }
  }

  /* ══════════════════════════════════════════════════
     CONSIGNOR / CONSIGNEE
  ══════════════════════════════════════════════════ */
  function handleCnorSel(k) {
    setCnorSel(k);
    if (!k) return;
    const d = allPartiesData[k];
    if (d) {
      setCnorData({ name: d.name, contact: d.contact, mobile: d.mobile, addr: d.addr, gst: d.gst, pin: d.pin });
      syncPartyNotifications(k);
    }
  }

  function handleCneeSel(k) {
    setCneeSel(k);
    if (!k) return;
    const d = allPartiesData[k];
    if (d) {
      setCneeData({ name: d.name, contact: d.contact, mobile: d.mobile, addr: d.addr, gst: d.gst, pin: d.pin });
      if (d.mobile) setOtpMobile(d.mobile);
    }
  }

  function handleToggleOTP(checked) {
    setOtpOn(checked);
    if (checked && cneeData.mobile) setOtpMobile(cneeData.mobile);
  }

  /* ══════════════════════════════════════════════════
     PIECES
  ══════════════════════════════════════════════════ */
  function addPiece() {
    setPieces(p => [...p, { id: pidCtr, desc: "", pkgType: "Box / Carton", qty: 1, l: 0, w: 0, h: 0, actW: 0 }]);
    setPidCtr(c => c + 1);
  }

  /* ══════════════════════════════════════════════════
     TOTALS & RATE
  ══════════════════════════════════════════════════ */
  function getTotals() {
    let tQ = 0, tA = 0, tV = 0, tC = 0;
       if (!isVolumetric) {
      tQ = parseInt(manualPcs) || 0;
      tC = parseFloat(manualChgWt) || 0;
      return { tQ, tA, tV, tC };
    }
    pieces.forEach(({ qty, l, w, h, actW }) => {
      const vt = ((l * w * h) / 5000) * qty;
      const at = actW * qty;
      tQ += qty; tA += at; tV += vt; tC += Math.max(at, vt);
    });
    return { tQ, tA, tV, tC };
  }

  function calcRate() {
    const { tQ, tC } = getTotals();
    const fp = parseFloat(fuelPct) || 0;
    const gp = parseFloat(gstPct) || 5;
    const ia = parseFloat(insAmt) || 0;
    const oa = parseFloat(otherAmt) || 0;

    // Try to get rate from API slab based on charged weight
    const slabMatch = getSlabRate(tC);
    let effectiveRate;
    if (slabMatch) {
      // Use rate amount directly from the matched slab
      effectiveRate = slabMatch.rateAmount;
    } else {
      // Fallback to manual base rate
      effectiveRate = parseFloat(baseRate) || 0;
    }

    const freight_ = rateBasis === "perkg" ? effectiveRate * tC
                   : rateBasis === "perpkg" ? effectiveRate * tQ
                   : effectiveRate;
    const fuel = freight_ * fp / 100;
    const sub = freight_ + fuel + ia + oa;
    const gst = sub * gp / 100;
    return {
      freight: freight_, fuel, ia, oa, sub, gst, total: sub + gst,
      effectiveRate, slabMatch
    };
  }

  const rate = calcRate();
  const { tQ, tA, tV, tC } = getTotals();
  const totals = getTotals();

  /* ══════════════════════════════════════════════════
     IMAGES
  ══════════════════════════════════════════════════ */
  function handleFiles(files) {
    Array.from(files).forEach((f) => {
      if (images.length >= 20) { addToast("Max 20 files allowed", "warn"); return; }
      if (f.size > 1 * 1024 * 1024) { addToast(f.name + " exceeds 1 MB limit", "err"); return; }
      const id = Date.now() + Math.random();
      const r = new FileReader();
      // store raw file object alongside preview — needed for FormData upload
      r.onload = (ev) => setImages(imgs => [...imgs, { id, name: f.name, type: imgType, src: ev.target.result, isImg: f.type.startsWith("image"), file: f }]);
      r.readAsDataURL(f);
    });
  }

  /* ══════════════════════════════════════════════════
     CONFIRM & BOOK (API save)
  ══════════════════════════════════════════════════ */
  function openConfirm() { setConfirmOpen(true); }

  async function doBook() {
    if (isBooking) return;
    if (!cnr.trim()) { addToast("Please select a Consignment Number", "warn"); return; }
    setIsBooking(true);
    addToast("Processing booking...", "info");
    try {
      const payload = {
        Party_Id: parseInt(partyKey) || null,
        Ref_No: "",
        Consignor_Name: cnorData.name,
        Consignee_Name: cneeData.name,
        // Book_Place: bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || bookingPlace,
        // Delivery_Place: deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || deliveryPlace,
        Book_Place: parseInt(bookingPlace) || null,
Delivery_Place: parseInt(deliveryPlace) || null,
        Weight: parseFloat(tC) || 0,
        No_Pkgs: parseInt(tQ) || 0,
        Company_Code: 1,
        FinYear_Code: 1,
        Created_By: 1,
        Booked: "Y",
        Consignment_No: rawConsNo,
        Book_Dt: bookingDate,
        Book_Time: new Date().toISOString(),
        Remarks: specialInst,
        Booking_Pincode: bookingPin,
        Delivery_Pincode: deliveryPin,
        Delivery_Date: expDelivery,
        Job_ID: parseInt(selectedJob) || null,
        Consignor_ID: parseInt(cnorSel) || null,
        Consignee_ID: parseInt(cneeSel) || null,
        Consignor_Contact: cnorData.contact,
        Consignor_Mob: cnorData.mobile ? cnorData.mobile.replace(/\D/g, "").slice(0, 15) : null,
        Consignor_Add: cnorData.addr,
        Consignor_GSTN: cnorData.gst,
        Consignor_Pincode: cnorData.pin,
        Consignee_Contact: cneeData.contact,
        Consignee_Mob: cneeData.mobile ? cneeData.mobile.replace(/\D/g, "").slice(0, 15) : null,
        Consignee_Add: cneeData.addr,
        Consignee_GSTN: cneeData.gst,
        Consignee_Pincode: cneeData.pin,
        FREIGHT_TERMS_ID: parseInt(freight) || null,
        Priority_ID: parseInt(priority) || null,
        Desc: commodity,
        Declared_value: parseFloat(declVal) || null,
        Special_Instruction: specialInst,
        No_of_pcs: parseInt(tQ) || 0,
        Charged_weight: parseFloat(tC) || 0,
        Rate_Basis: rateBasis,
        Rate: parseFloat(baseRate) || 0,
        fuel_surcharge: parseInt(fuelPct) || 0,
        GST: parseInt(gstPct) || 0,
        Other_charges: parseFloat(otherAmt) || 0,
        Freight_Charges: rate.freight,

        fuel_surcharge_rate: rate.fuel,
        GST_Amt: rate.gst,
        // Other_charges_rate: rate.oa,
        Taxable_Amt: rate.sub,
        Total_Amt: rate.total,
        Insurance: parseFloat(insAmt) || 0,
                VolumetricDetails: isVolumetric ? pieces.map(p => {
          const qty = +p.qty || 0, l = +p.l || 0, w = +p.w || 0, h = +p.h || 0, actW = +p.actW || 0;
          const volTot = (l * w * h / 5000) * qty;
          const actTot = actW * qty;
          const chgTot = Math.max(actTot, volTot);
          return {
            Description: p.desc,
            Pkg_Type: p.pkgType,
            Qty: qty,
            Length: l,
            Width: w,
            Height: h,
            Act_Wt_pc: actW,
            Vol_Wt_kg: volTot,
            Charged_Wt_kg: chgTot
          };
        }) : []
      };

      const response = await api.post('/save-cons-booking', [payload]);

      if (response.data.success) {

        if (images.length > 0) {

      // Step 1 — CNR number as folder name
      const consNo = cnr;   // e.g. STL-2506-00123

      // Step 2 — group by type
      const byType = {};
      images.forEach(img => {
        if (!byType[img.type]) byType[img.type] = [];
        byType[img.type].push(img);
      });

      // Step 3 & 4 — build FormData and call API for each type
      const uploadPromises = Object.entries(byType).map(([type, imgs]) => {
        const fd = new FormData();
        fd.append("consignment_no", consNo);
        fd.append("image_type",     type);
        imgs.forEach(img => fd.append("images", img.file, img.name));

        return api.post("/upload-cons-images", fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      });

      // Step 5 — wait for all uploads to finish
      try {
        await Promise.all(uploadPromises);
        addToast(`${images.length} image(s) uploaded`, "ok");
      } catch (imgErr) {
        addToast("Booking saved but images failed to upload", "warn");
      }
    }
        setConfirmOpen(false);
        const sd = {
          cnr,
          from: bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || PLACE_NAMES[bookingPlace] || "ORIGIN",
          to:   deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || PLACE_NAMES[deliveryPlace] || "DEST",
          cnor: cnorData.name || "—",
          cnee: cneeData.name || "—",
          pcs:  tQ + " pcs",
          wt:   tC.toFixed(2) + " kg",
          amt:  "₹ " + fmt(rate.total),
          date: todayStr,
        };
        setStickerData(sd);
        setBookedCnr(cnr);
        setSuccessOpen(true);
        addToast("Consignment booked successfully!", "ok");
        fetchAvailableConsignments(); // remove used CNR from dropdown
      } else {
        addToast(response.data.message || "Booking failed", "err");
      }
    } catch (error) {
      console.error("Booking error:", error);
      addToast(error.response?.data?.message || "Failed to save booking", "err");
    } finally {
      setIsBooking(false);
    }
  }

  /* ── Barcode ── */
  useEffect(() => {
    if (stickerOpen && barcodeSvgRef.current && cnr) {
      setTimeout(() => {
        try {
          if (window.JsBarcode) {
            window.JsBarcode(barcodeSvgRef.current, cnr, {
              format: "CODE128", width: 2, height: 52, displayValue: false, margin: 4,
              lineColor: "#1A2940", background: "transparent",
            });
          }
        } catch (e) { }
      }, 100);
    }
  }, [stickerOpen, cnr]);

  /* ── Print sticker ── */
  function printSticker() {
    const c = document.getElementById("cb-sticker-print-inner").innerHTML;
    const w = window.open("", "_blank", "width=480,height=600");
    w.document.write(`<html><head><link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Lora:wght@700&display=swap" rel="stylesheet">
  <style>body{font-family:'Plus Jakarta Sans',sans-serif;margin:0;padding:20px;background:#fff;}
  .cb-sl-logo{display:flex;align-items:center;gap:8px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #ddd;}
  .cb-sl-name{font-family:'Lora',serif;font-size:16px;font-weight:700;}
  .cb-s-cnr{font-size:22px;font-weight:800;letter-spacing:2px;text-align:center;margin:8px 0;}
  .cb-s-route{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;margin:10px 0;justify-content:center;}
  .cb-s-info{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:10px;}
  .cb-s-cell{background:#EBF4FF;border-radius:6px;padding:7px 9px;}
  .cb-s-cell-l{font-size:9px;font-weight:800;text-transform:uppercase;color:#8AA5C0;}
  .cb-s-cell-v{font-size:12px;font-weight:700;}svg{max-width:100%;}.cb-sl-mark{display:none;}</style>
  </head><body>${c}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  }

  /* ── Clear form ── */
  function clearForm() {
    setBookingPlace(""); setBookingPin(""); setBpinInfo("");
    setDeliveryPlace(""); setDeliveryPin(""); setDpinInfo("");
    setPriority(""); setExpDelivery(calcExpDeliveryDate("standard", bookingDate));
    setPartyKey(""); setPartyGst(""); setSelectedJob(null); setFreight(""); setPartyJobs([]);
    setCnorSel(""); setCnorData(emptyParty); setCnorMode("list");
    setCneeSel(""); setCneeData(emptyParty); setCneeMode("list");
    setOtpOn(false); setOtpMobile(""); setOtpChannel("both"); setOtpValidity("30"); setOtpAttempts("3");
    setNotifMaster(false); setSmsOn(true); setEmailOn(true); setWaOn(false);
    setSmsNum(""); setEmailAddr(""); setWaNum(""); setSelectedEvents([]);
    setPieces([]); setCommodity(""); setDeclVal(""); setSpecialInst("");
    setRateBasis("perkg"); setBaseRate(0); setFuelPct(12); setGstPct("5"); setInsAmt(0); setOtherAmt(0);
    setImages([]);
    setRawConsNo(null);
    setRawPrefix("");
    addToast("Form cleared", "");
  }

  function newBooking() {
    setStickerOpen(false);
    clearForm();
    setStep(1);
    fetchAvailableConsignments();
  }

  /* ══════════════════════════════════════════════════
     BUILD SELECT OPTIONS (API first, static fallback)
  ══════════════════════════════════════════════════ */
  const placeOpts = [
    { value: "", label: "" },
    ...(bookingPlaceOpts.length > 0
      ? bookingPlaceOpts
      : Object.keys(PLACE_NAMES).map(k => ({ value: k, label: PLACE_NAMES[k] })))
  ];
  const delivPlaceOpts = [
    { value: "", label: "" },
    ...(deliveryPlaceOpts.length > 0
      ? deliveryPlaceOpts
      : Object.keys(PLACE_NAMES).map(k => ({ value: k, label: PLACE_NAMES[k] })))
  ];
  const partySelectOpts = [
    { value: "", label: "" },
    ...(partyOpts.length > 0
      ? partyOpts
      : Object.keys(PARTIES).map(k => ({ value: k, label: PARTIES[k].name })))
  ];

      const freightSelectOpts = freightTermOpts.length > 0
  ? freightTermOpts.map(o => ({
      value: o.freighT_TERMS_ID || o.id || o.FREIGHT_TERMS_ID,
      label: o.freighT_TERMS || o.name || o.FREIGHT_TERMS || o.label
    }))
  : [
      { value: "prepaid", label: "Prepaid (Sender pays)" },
      { value: "topay", label: "To Pay (Receiver)" },
      { value: "foc", label: "FOC (Free of Charge)" }
    ];
  const prioritySelectOpts = priorityOpts.length > 0
    ?priorityOpts.map(o => ({
        value: o.priority_ID || o.id || o.Priority_ID,
        label: o.priority_Value || o.value || o.Priority_Value
      }))
    : [
        { value: "standard", label: "Standard (3 days)" },
        { value: "express",  label: "Express (24 hrs)" },
        { value: "urgent",   label: "Urgent (Same Day)" },
      ];
  const cnorSelectOpts = [{ value: "", label: "" }, ...cnorOpts];
  const cneeSelectOpts = [{ value: "", label: "" }, ...cneeOpts];
  const jobOpts = partyKey
    ? (partyJobs.length > 0
        ? partyJobs.map(j => ({ value: j.job_Id || j.Job_Id, label: j.job_Name || j.Job_Name }))
        : (PARTY_JOBS[partyKey] || []).map(j => ({ value: j, label: j })))
    : [];
  const rateOpts       = [{ value: "perkg", label: "Per Kg" }, { value: "perpkg", label: "Per Package" }, { value: "fixed", label: "Fixed Rate" }];
  const gstOpts        = [{ value: "0", label: "0% (Exempt)" }, { value: "5", label: "5%" }, { value: "12", label: "12%" }, { value: "18", label: "18%" }];
  const otpChOpts      = [{ value: "both", label: "SMS + WhatsApp" }, { value: "sms", label: "SMS Only" }, { value: "wa", label: "WhatsApp Only" }];
  const otpValOpts     = [{ value: "15", label: "15 minutes" }, { value: "30", label: "30 minutes" }, { value: "60", label: "1 hour" }];
  const otpAttOpts     = [{ value: "3", label: "3 attempts" }, { value: "5", label: "5 attempts" }];

  /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
  return (
    <div className="cb-root" onKeyDown={handleEnterKey}>
{/* 
      ── HEADER ──
      <header className="cb-hdr">
        <div className="cb-logo">
          <div className="cb-logo-mark">
            <svg viewBox="0 0 24 24">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
            </svg>
          </div>
          <div>
            <div className="cb-logo-name">Saami <span>Tradestar</span> Logistics</div>
            <div className="cb-logo-pvt">Pvt Ltd</div>
          </div>
        </div>
        <div className="cb-hdr-r">
          <span className="cb-hdr-pill">New Booking</span>
          <div className="cb-hdr-user">
            <div className="cb-hdr-av">RM</div>Rahul M.
          </div>
        </div>
      </header> */}

      {/* ── BREADCRUMB ── */}
      {/* <div className="cb-bc">
        <span>Operations</span>
        <span className="cb-bc-sep">›</span>
        <span>Consignments</span>
        <span className="cb-bc-sep">›</span>
        <span className="cb-bc-cur">New Booking</span>
      </div> */}

      <div className="cb-main">

        {/* ── PAGE TITLE ── */}
        <div className="cb-pg-row">
          <div className="cb-pg-title">
            <h1>New Consignment Booking</h1>
            <p>Fill shipment details to generate a consignment number and barcode sticker</p>
          </div>
        </div>

        {/* ── CNR BANNER ── */}
        <div className="cb-cnr-banner">
          <div style={{ flex: 1 }}>
            <div className="cb-cnr-lbl" style={{ textAlign: "left", marginLeft: 4 }}>Consignment Number</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <input
                className="cb-cnr-inp"
                type="text"
                value={cnr}
                readOnly
                placeholder={cnrLoading ? "Loading…" : "No consignment available"}
                maxLength={30}
                style={{ width: "100%", maxWidth: 340, cursor: "default" }}
              />
              <span style={{ fontSize: 16, opacity: 0.7, cursor: "default", marginLeft: 4 }} title="Auto-assigned consignment number">✎</span>
            </div>
            {!cnrLoading && !cnr && (
              <div style={{ fontSize: 11.5, color: "#E53E3E", marginTop: 5, marginLeft: 2 }}>
                ⚠ No available consignment numbers. Please issue new consignments first.
              </div>
            )}
          </div>
          <div className="cb-cnr-r">
            <div>
              <div className="cb-cnr-date-lbl">Booking Date</div>
              <input
                type="date"
                value={bookingDate}
                onChange={e => handleDateChange(e.target.value)}
                style={{ background: "transparent", border: "none", color: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              />
            </div>
          </div>
        </div>

        {/* ── STEPPER ── */}
        <div className="cb-stepper" ref={stepperRef}>
          {[
            { n: 1, name: "Route & Party",        desc: "Origin, destination & account" },
            { n: 2, name: "Consignor / Consignee", desc: "Sender, receiver & OTP" },
            { n: 3, name: "Shipment & Rate",       desc: "Pieces, images & billing" },
          ].map((s, i, arr) => (
            <>
              <div
                key={s.n}
                className={`cb-step${step === s.n ? " cb-step--active" : step > s.n ? " cb-step--done" : ""}`}
                onClick={() => setStep(s.n)}
              >
                <div className="cb-step-num">{step > s.n ? "✓" : s.n}</div>
                <div>
                  <div className="cb-step-name">{s.name}</div>
                  <div className="cb-step-desc">{s.desc}</div>
                </div>
              </div>
              {i < arr.length - 1 && <div key={`div-${s.n}`} className="cb-step-div" />}
            </>
          ))}
        </div>

        {/* ════════ STEP 1 ════════ */}
        {step === 1 && (
          <div>
            {/* Route */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Route Information</div>
                  <div className="cb-band-sub">Select origin, destination &amp; expected delivery</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g3">
                  <div className="cb-field">
                    <label className="req">Booking Place</label>
                    <AutoComplete options={placeOpts} value={bookingPlace} onChange={v => handlePlaceChange("booking", v)} placeholder="Select branch…" />
                    {bpinInfo && <div className="cb-pin-tag on">{bpinInfo}</div>}
                  </div>
                  <div className="cb-field">
                    <label className="req">Booking Pincode</label>
                    <input value={bookingPin} onChange={(e) => setBookingPin(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" maxLength={6} />
                  </div>
                  <div className="cb-field">
                    <label>Booking Date</label>
                    <input type="date" value={bookingDate} onChange={e => handleDateChange(e.target.value)} />
                  </div>
                  <div className="cb-field">
                    <label className="req">Delivery Place</label>
                    <AutoComplete options={delivPlaceOpts} value={deliveryPlace} onChange={v => handlePlaceChange("delivery", v)} placeholder="Select destination…" />
                    {dpinInfo && <div className="cb-pin-tag on">{dpinInfo}</div>}
                  </div>
                  <div className="cb-field">
                    <label className="req">Destination Pincode</label>
                    <input value={deliveryPin} onChange={(e) => setDeliveryPin(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" maxLength={6} />
                  </div>
                  <div className="cb-field">
                    <label>Expected Delivery</label>
                    <input type="date" value={expDelivery} onChange={e => setExpDelivery(e.target.value)} placeholder="Auto-calculated" />
                  </div>
                </div>
              </div>
            </div>

            {/* Party */}
            <div className="cb-card">
              <div className="cb-band cb-band--teal">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Party &amp; Department</div>
                  <div className="cb-band-sub">Select billing account — one job per consignment</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g2">
                  <div className="cb-field">
                    <label className="req">Party / Account</label>
                    <AutoComplete options={partySelectOpts} value={partyKey} onChange={handlePartyChange} placeholder="Search party…" />
                  </div>
                  <div className="cb-field">
                    <label>Party GSTIN</label>
                    <input value={partyGst} readOnly className="ro" placeholder="Auto-filled from party" />
                  </div>
                  <div className="cb-field cb-gc2">
                    <label className="req">Job / Department</label>
                    <div className="cb-jobs-wrap">
                      <div className="cb-jobs-hdr">
                        <span>{partyKey ? (partyOpts.find(o => String(o.value) === String(partyKey))?.label || PARTIES[partyKey]?.name || partyKey) : "Select a party to load jobs"}</span>
                        <span style={{ color: "var(--blue)" }}>
                          {selectedJob ? (jobOpts.find(o => String(o.value) === String(selectedJob))?.label || selectedJob) : "None selected"}
                        </span>
                      </div>
                      <div className="cb-jobs-body">
                        {!partyKey ? (
                          <span className="cb-jobs-empty-txt">Select a party first…</span>
                        ) : jobOpts.length === 0 ? (
                          <span className="cb-jobs-empty-txt">No jobs found</span>
                        ) : (
                          jobOpts.map((j) => (
                            <button
                              key={j.value}
                              className={`cb-job-chip${String(selectedJob) === String(j.value) ? " sel" : ""}`}
                              onClick={() => setSelectedJob(j.value)}
                            >{j.label}</button>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cb-field">
                    <label>Freight Terms</label>
                    <AutoComplete options={freightSelectOpts} value={freight} onChange={setFreight}  placeholder="Select"/>
                  </div>
                  <div className="cb-field">
                    <label>Priority</label>
                    <AutoComplete options={prioritySelectOpts} value={priority} onChange={handlePriorityChange} placeholder="Select" />
                  </div>
                </div>
              </div>
            </div>

            <div className="cb-actions">
              <button className="cb-btn cb-btn--ghost" onClick={clearForm}>✕ Clear</button>
              <button className="cb-btn cb-btn--primary" onClick={() => setStep(2)}>
                Next — Consignor / Consignee
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* ════════ STEP 2 ════════ */}
        {step === 2 && (
          <div>
            <div className="cb-g2">
              {/* CONSIGNOR */}
              <div className="cb-card">
                <div className="cb-band">
                  <div className="cb-band-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>
                  </div>
                  <div><div className="cb-band-title">Consignor</div><div className="cb-band-sub">Who is dispatching the goods</div></div>
                </div>
                <div className="cb-card-body">
                  <div className="cb-pb">
                    <div className="cb-pb-hdr">
                      <span className="cb-pb-lbl">Entry Mode</span>
                      <div className="cb-mode-tabs">
                        <button className={`cb-mode-tab${cnorMode === "list" ? " active" : ""}`} onClick={() => setCnorMode("list")}>From List</button>
                        <button className={`cb-mode-tab${cnorMode === "direct" ? " active" : ""}`} onClick={() => setCnorMode("direct")}>Direct Entry</button>
                      </div>
                    </div>
                    <div className="cb-pb-body">
                      {cnorMode === "list" && (
                        <div className="cb-field">
                          <label>Select Consignor</label>
                          <AutoComplete options={cnorSelectOpts} value={cnorSel} onChange={handleCnorSel} placeholder="Search consignor…" />
                        </div>
                      )}
                      <div className="cb-field"><label className="req">Name</label><input value={cnorData.name} onChange={(e) => setCnorData(d => ({ ...d, name: e.target.value }))} placeholder="Company / person name" /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>Contact Person</label><input value={cnorData.contact} onChange={(e) => setCnorData(d => ({ ...d, contact: e.target.value }))} placeholder="Contact name" /></div>
                        <div className="cb-field"><label>Mobile</label><input value={cnorData.mobile} onChange={(e) => setCnorData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))} placeholder="10-digit" maxLength={10} /></div>
                      </div>
                      <div className="cb-field"><label>Address</label><textarea value={cnorData.addr} onChange={(e) => setCnorData(d => ({ ...d, addr: e.target.value }))} placeholder="Full address" rows={2} /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>GSTIN</label><input value={cnorData.gst} onChange={(e) => setCnorData(d => ({ ...d, gst: e.target.value }))} placeholder="15-char GST" maxLength={15} /></div>
                        <div className="cb-field"><label>Pincode</label><input value={cnorData.pin} onChange={(e) => setCnorData(d => ({ ...d, pin: e.target.value.replace(/\D/g, "") }))} placeholder="6-digit" maxLength={6} /></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CONSIGNEE */}
              <div className="cb-card">
                <div className="cb-band cb-band--teal">
                  <div className="cb-band-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <div><div className="cb-band-title">Consignee</div><div className="cb-band-sub">Who will receive the shipment</div></div>
                </div>
                <div className="cb-card-body">
                  <div className="cb-pb">
                    <div className="cb-pb-hdr">
                      <span className="cb-pb-lbl">Entry Mode</span>
                      <div className="cb-mode-tabs">
                        <button className={`cb-mode-tab${cneeMode === "list" ? " active" : ""}`} onClick={() => setCneeMode("list")}>From List</button>
                        <button className={`cb-mode-tab${cneeMode === "direct" ? " active" : ""}`} onClick={() => setCneeMode("direct")}>Direct Entry</button>
                      </div>
                    </div>
                    <div className="cb-pb-body">
                      {cneeMode === "list" && (
                        <div className="cb-field">
                          <label>Select Consignee</label>
                          <AutoComplete options={cneeSelectOpts} value={cneeSel} onChange={handleCneeSel} placeholder="Search consignee…" />
                        </div>
                      )}
                      <div className="cb-field"><label className="req">Name</label><input value={cneeData.name} onChange={(e) => setCneeData(d => ({ ...d, name: e.target.value }))} placeholder="Company / person name" /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>Contact Person</label><input value={cneeData.contact} onChange={(e) => setCneeData(d => ({ ...d, contact: e.target.value }))} placeholder="Contact name" /></div>
                        <div className="cb-field"><label>Mobile</label><input value={cneeData.mobile} onChange={(e) => setCneeData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))} placeholder="10-digit" maxLength={10} /></div>
                      </div>
                      <div className="cb-field"><label>Address</label><textarea value={cneeData.addr} onChange={(e) => setCneeData(d => ({ ...d, addr: e.target.value }))} placeholder="Full address" rows={2} /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>GSTIN</label><input value={cneeData.gst} onChange={(e) => setCneeData(d => ({ ...d, gst: e.target.value }))} placeholder="15-char GST" maxLength={15} /></div>
                        <div className="cb-field"><label>Pincode</label><input value={cneeData.pin} onChange={(e) => setCneeData(d => ({ ...d, pin: e.target.value.replace(/\D/g, "") }))} placeholder="6-digit" maxLength={6} /></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* OTP */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">OTP Delivery Confirmation</div>
                  <div className="cb-band-sub">Optional — require OTP from consignee at delivery point</div>
                </div>
              </div>
              <div className="cb-card-body" style={{ paddingBottom: 0 }}>
                <div className="cb-otp-wrap">
                  <div className="cb-otp-hdr">
                    <div className="cb-otp-hdr-l">
                      <Toggle checked={otpOn} onChange={handleToggleOTP} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>Enable OTP for Delivery</div>
                        <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Consignee must provide OTP before goods are handed over</div>
                      </div>
                    </div>
                    <span className="cb-otp-status">{otpOn ? "On" : "Off"}</span>
                  </div>
                  {otpOn && (
                    <div className="cb-otp-body">
                      <div className="cb-otp-info">🔐 An OTP is generated at delivery and sent to consignee mobile. Agent must verify before handing over.</div>
                      <div className="cb-g2">
                        <div className="cb-field"><label className="req">Consignee OTP Mobile</label><input value={otpMobile} onChange={(e) => setOtpMobile(e.target.value.replace(/\D/g, ""))} placeholder="10-digit number" maxLength={10} /></div>
                        <div className="cb-field"><label>OTP Channel</label><AutoComplete options={otpChOpts} value={otpChannel} onChange={setOtpChannel} /></div>
                        <div className="cb-field"><label>OTP Validity</label><AutoComplete options={otpValOpts} value={otpValidity} onChange={setOtpValidity} /></div>
                        <div className="cb-field"><label>Max Attempts</label><AutoComplete options={otpAttOpts} value={otpAttempts} onChange={setOtpAttempts} /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="cb-card">
              <div className="cb-band cb-band--teal" style={{ padding: "0 18px", minHeight: 52 }}>
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Booking Notifications</div>
                  <div className="cb-band-sub">Optional — notify consignor &amp; consignee at each milestone</div>
                </div>
                <div className="cb-band-r">
                  <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.55)" }}>{notifMaster ? "ON" : "OFF"}</span>
                  <Toggle checked={notifMaster} onChange={setNotifMaster} />
                </div>
              </div>
              {notifMaster && (
                <div className="cb-notif-grid">
                  {/* SMS */}
                  <div className={`cb-nc${smsOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico sms-ico">📱</div>
                      <div className="cb-nc-title">{getTitle(1, "SMS")}</div>
                      <Toggle checked={smsOn} onChange={setSmsOn} />
                    </div>
                    {smsOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>Mobile No.</label><input value={smsNum} onChange={(e) => setSmsNum(e.target.value)} placeholder="10-digit" /></div>
                        {notificationEvents.filter(e => (e.event_id || e.Event_id || e.Event_Id) === 1).map(e => {
                          const id = e.notification_Event_id || e.id || e.Id;
                          const name = e.notification_Event_Desc || e.name || e.Name;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                  {/* Email */}
                  <div className={`cb-nc${emailOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico em-ico">✉️</div>
                      <div className="cb-nc-title">{getTitle(2, "Email")}</div>
                      <Toggle checked={emailOn} onChange={setEmailOn} />
                    </div>
                    {emailOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>Email Address</label><input value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="email@company.com" /></div>
                        {notificationEvents.filter(e => (e.event_id || e.Event_id || e.Event_Id) === 2).map(e => {
                          const id = e.notification_Event_id || e.id || e.Id;
                          const name = e.notification_Event_Desc || e.name || e.Name;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                  {/* WhatsApp */}
                  <div className={`cb-nc${waOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico wa-ico">💬</div>
                      <div className="cb-nc-title">{getTitle(3, "WhatsApp")}</div>
                      <Toggle checked={waOn} onChange={setWaOn} />
                    </div>
                    {waOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>WhatsApp No.</label><input value={waNum} onChange={(e) => setWaNum(e.target.value)} placeholder="91XXXXXXXXXX" /></div>
                        {notificationEvents.filter(e => (e.event_id || e.Event_id || e.Event_Id) === 3).map(e => {
                          const id = e.notification_Event_id || e.id || e.Id;
                          const name = e.notification_Event_Desc || e.name || e.Name;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="cb-actions">
              <button className="cb-btn cb-btn--ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="cb-btn cb-btn--primary" onClick={() => setStep(3)}>
                Next — Shipment Details
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* ════════ STEP 3 ════════ */}
        {step === 3 && (
          <div>
            {/* Pieces */}
            <div className="cb-card">
              <div className="cb-band cb-band--amber">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Piece-wise Shipment Details</div>
                  <div className="cb-band-sub">Vol. Wt = L×W×H ÷ 5000 × Qty &nbsp;|&nbsp; Charged = higher of Actual vs Volumetric</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g3" style={{ marginBottom: 14 }}>
                  <div className="cb-field cb-gc2">
                    <label className="req">Commodity / Description</label>
                    <input value={commodity} onChange={(e) => setCommodity(e.target.value)} placeholder="e.g. Engine Parts, Brake Pads, Clutch Assemblies" />
                  </div>
                  <div className="cb-field">
                    <label>Declared Value (₹)</label>
                    <input type="number" value={declVal} onChange={(e) => setDeclVal(e.target.value)} placeholder="0" />
                  </div>
                  <div className="cb-field cb-gc3">
                    <label>Special Instructions</label>
                    <input value={specialInst} onChange={(e) => setSpecialInst(e.target.value)} placeholder="e.g. Fragile — Handle with care, Keep upright…" />
                  </div>
                  
                          <div className="field"><label className="req">No. of PCs</label><input type="number" value={isVolumetric ? totals.tQty : manualPcs} disabled={isVolumetric} placeholder="0" onChange={e => setManualPcs(e.target.value)} /></div>
                <div className="field"><label className="req">Charged Weight</label><input type="number" value={isVolumetric ? totals.tChg : manualChgWt} disabled={isVolumetric} placeholder="0" onChange={e => setManualChgWt(e.target.value)} /></div>
                <div className="field">
                  <label>&nbsp;</label>
                  <div style={{ display: 'flex', alignItems: 'center', height: '38px' }}>
                    <label className="cb-sw" style={{ marginRight: '8px' }}>
                      <input type="checkbox" checked={isVolumetric} onChange={e => setIsVolumetric(e.target.checked)} />
                      <span className="cb-sw-track"></span>
                    </label>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)' }}>Volumetric Weight</span>
                  </div>
                </div>
                </div>

         {isVolumetric && (
                          <>

                <div className="cb-piece-scroll">
                  <table className="cb-pt">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{ textAlign: "left", minWidth: 128 }}>Description</th>
                        <th style={{ minWidth: 108 }}>Pkg Type</th>
                        <th style={{ minWidth: 55 }}>Qty</th>
                        <th style={{ minWidth: 64 }}>L (cm)</th>
                        <th style={{ minWidth: 64 }}>W (cm)</th>
                        <th style={{ minWidth: 64 }}>H (cm)</th>
                        <th style={{ minWidth: 76 }}>Act Wt/pc</th>
                        <th className="cb-vol-cell" style={{ minWidth: 82 }}>Vol Wt (kg)</th>
                        <th className="cb-chg-cell" style={{ minWidth: 86 }}>Chg Wt (kg)</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pieces.map((row, i) => (
                        <PieceRow
                          key={row.id}
                          idx={i}
                          row={row}
                          onChange={(updated) => setPieces(p => p.map(x => x.id === row.id ? updated : x))}
                          onDelete={() => setPieces(p => p.filter(x => x.id !== row.id))}
                        />
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="row-sub">
                        <td colSpan={3} className="tot-lbl">Sub Total</td>
                        <td>{tQ}</td>
                        <td colSpan={3}></td>
                        <td>{tA.toFixed(2)} kg</td>
                        <td>{tV.toFixed(2)} kg</td>
                        <td style={{ color: "var(--blue)" }}>{tC.toFixed(2)} kg</td>
                        <td></td>
                      </tr>
                      <tr className="row-grand">
                        <td colSpan={3} style={{ textAlign: "left", paddingLeft: 10 }}>GRAND TOTAL</td>
                        <td style={{ fontSize: 15 }}>{tQ}</td>
                        <td colSpan={3}></td>
                        <td style={{ fontSize: 15 }}>{tA.toFixed(2)} kg</td>
                        <td style={{ fontSize: 15 }}>{tV.toFixed(2)} kg</td>
                        <td style={{ fontSize: 15, color: "#5eead4" }}>{tC.toFixed(2)} kg</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="cb-pt-bar">
                    <span>💡 Volumetric Weight = L×W×H ÷ 5000 per piece</span>
                    <div style={{ display: "flex", gap: 7 }}>
                      <button className="cb-btn cb-btn--ghost cb-btn--sm" onClick={addPiece}>+ Add Piece</button>
                      <button className="cb-btn cb-btn--ghost cb-btn--sm" style={{ color: "var(--red)" }} onClick={() => setPieces([])}>Clear All</button>
                    </div>
                  </div>
                </div>
                <div className="cb-wt-strip">
                  <div className="cb-wt-box bx-blue"><div className="cb-wb-lbl">Total Pieces</div><div className="cb-wb-val">{tQ}</div><div className="cb-wb-unit">packages</div></div>
                  <div className="cb-wt-box bx-amber"><div className="cb-wb-lbl">Actual Weight</div><div className="cb-wb-val">{tA.toFixed(2)}</div><div className="cb-wb-unit">kg</div></div>
                  <div className="cb-wt-box bx-blue"><div className="cb-wb-lbl">Volumetric Weight</div><div className="cb-wb-val">{tV.toFixed(2)}</div><div className="cb-wb-unit">kg</div></div>
                  <div className="cb-wt-box bx-teal"><div className="cb-wb-lbl">✓ Charged Weight</div><div className="cb-wb-val">{tC.toFixed(2)}</div><div className="cb-wb-unit">kg — billing basis</div></div>
                </div>
              </>
              )}
            </div>
           </div>


            {/* Images */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Shipment Images</div>
                  <div className="cb-band-sub">Attach photos of packages, labels, invoices or damage evidence</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-img-type-row">
                  {["Package", "Label", "Invoice", "Damage", "Other"].map((t) => (
                    <button key={t} className={`cb-itype${imgType === t ? " active" : ""}`} onClick={() => setImgType(t)}>
                      {t === "Package" ? "📦" : t === "Label" ? "🏷" : t === "Invoice" ? "📄" : t === "Damage" ? "⚠️" : "📎"} {t}
                    </button>
                  ))}
                </div>
                <div
                  className="cb-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag"); handleFiles(e.dataTransfer.files); }}
                >
                  <input type="file" accept="image/*,.pdf" multiple onChange={(e) => handleFiles(e.target.files)} />
                  <div className="cb-dz-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>Drag &amp; drop here, or click to browse</div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Type: <strong>{imgType}</strong> &nbsp;|&nbsp; JPG · PNG · PDF · Max 5 MB each</div>
                </div>
                {images.length > 0 && (
                  <div className="cb-img-grid">
                    {images.map((img) => (
                      <div key={img.id} className="cb-img-item">
                        {img.isImg
                          ? <img src={img.src} alt={img.name} />
                          : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", fontSize: 26 }}>📄</div>}
                        <span className="cb-img-type-tag">{img.type}</span>
                        <div className="cb-img-lbl">{img.name}</div>
                        <button className="cb-img-del" onClick={() => setImages(imgs => imgs.filter(x => x.id !== img.id))}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {images.length > 0 && (
                  <div className="cb-img-count-bar">
                    <span>{images.length} file{images.length !== 1 ? "s" : ""} attached</span>
                    <button className="cb-btn cb-btn--ghost cb-btn--sm" style={{ color: "var(--red)" }} onClick={() => setImages([])}>Remove All</button>
                  </div>
                )}
              </div>
            </div>

            {/* Rate */}
            <div className="cb-card">
              <div className="cb-band cb-band--green">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Rate Calculation</div>
                  <div className="cb-band-sub">Auto-computed from party rate card — override allowed with approval</div>
                </div>
              </div>
              <div className="cb-card-body">
                {/* Rate Slab Info Banner */}
                {rateLoading && (
                  <div style={{ padding: "8px 14px", background: "#e8f4fd", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#1976d2", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span>
                    Loading rate details…
                  </div>
                )}
                {fetchedRateHeader && !rateLoading && (
                  <div style={{ padding: "10px 14px", background: "linear-gradient(135deg, #e8f5e9, #f1f8e9)", borderRadius: 8, marginBottom: 12, fontSize: 12, border: "1px solid #c8e6c9" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 15 }}>✅</span>
                      <strong style={{ color: "#2e7d32" }}>Rate Card Loaded</strong>
                      {fetchedRateHeader.currency_Code && <span style={{ marginLeft: "auto", background: "#fff", padding: "2px 8px", borderRadius: 4, fontWeight: 600, color: "#1b5e20" }}>{fetchedRateHeader.currency_Code}</span>}
                    </div>
                    <div style={{ color: "#4a4a4a", lineHeight: 1.6 }}>
                      {fetchedRateHeader.validity_Name && <span>Validity: <strong>{fetchedRateHeader.validity_Name}</strong> · </span>}
                      {fetchedRateHeader.effective_From && <span>Effective: <strong>{formatDateDisplay(fetchedRateHeader.effective_From)}</strong></span>}
                      {fetchedRateHeader.effective_To && <span> → <strong>{formatDateDisplay(fetchedRateHeader.effective_To)}</strong></span>}
                      {fetchedRateHeader.min_Charg_Wgt > 0 && <span> · Min Chg Wt: <strong>{fetchedRateHeader.min_Charg_Wgt} kg</strong></span>}
                    </div>
                    {fetchedRateHeader.rate_Remark && <div style={{ marginTop: 4, fontStyle: "italic", color: "#666" }}>📝 {fetchedRateHeader.rate_Remark}</div>}
                  </div>
                )}
                {!fetchedRateHeader && !rateLoading && partyKey && bookingPlace && deliveryPlace && (
                  <div style={{ padding: "8px 14px", background: "#fff3e0", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#e65100", border: "1px solid #ffe0b2" }}>
                    ⚠️ No rate card found for this party/route. Using manual base rate.
                  </div>
                )}

                {/* Rate Slab Table */}
                {fetchedRateDetails.length > 0 && (
                  <div style={{ marginBottom: 16, overflowX: "auto" }}>
                    <table className="cb-tbl" style={{ fontSize: 11.5, width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "6px 10px" }}>Slab</th>
                          <th style={{ padding: "6px 10px" }}>From (kg)</th>
                          <th style={{ padding: "6px 10px" }}>To (kg)</th>
                          <th style={{ padding: "6px 10px" }}>Rate (₹)</th>
                          <th style={{ padding: "6px 10px" }}>Addl Wt Rate</th>
                          <th style={{ padding: "6px 10px" }}>UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fetchedRateDetails.map((d, i) => {
                          const fromW = parseFloat(d.from_Wgt ?? d.From_Wgt) || 0;
                          const toW = parseFloat(d.to_Wgt ?? d.To_Wgt) || 0;
                          const isActive = tC >= fromW && tC <= toW;
                          return (
                            <tr key={d.rate_detailId ?? d.Rate_detailId ?? i}
                                style={isActive ? { background: "#e8f5e9", fontWeight: 600 } : {}}>
                              <td style={{ padding: "5px 10px", textAlign: "center" }}>{i + 1}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>{fromW.toFixed(2)}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>{toW.toFixed(2)}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right", color: isActive ? "#2e7d32" : "inherit" }}>
                                ₹ {(parseFloat(d.rateAmount ?? d.RateAmount) || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>
                                {(parseFloat(d.addl_Wgt ?? d.Addl_Wgt) || 0) > 0
                                  ? `₹ ${(parseFloat(d.addl_Wgt ?? d.Addl_Wgt)).toFixed(2)}`
                                  : "—"}
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "center" }}>{d.uoM_Code ?? d.UOM_Code ?? "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {rate.slabMatch && (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: "#2e7d32", fontWeight: 500 }}>
                        ✓ Matched slab: {rate.slabMatch.fromWgt}–{rate.slabMatch.toWgt} kg → ₹ {rate.effectiveRate.toFixed(2)} / unit
                      </div>
                    )}
                  </div>
                )}

                <div className="cb-g2">
                  <div className="cb-g2">
                    <div className="cb-field"><label>Rate Basis</label><AutoComplete options={rateOpts} value={rateBasis} onChange={setRateBasis} /></div>
                    <div className="cb-field">
                      <label>Base Rate (₹) {rate.slabMatch ? <span style={{ fontSize: 10, color: "#2e7d32", fontWeight: 500 }}> — auto from slab</span> : ""}</label>
                      <input type="number" value={rate.slabMatch ? rate.effectiveRate.toFixed(2) : baseRate} onChange={(e) => setBaseRate(e.target.value)} placeholder="Auto from rate card" readOnly={!!rate.slabMatch} style={rate.slabMatch ? { background: "#f1f8e9", fontWeight: 600 } : {}} />
                    </div>
                    <div className="cb-field"><label>Fuel Surcharge %</label><input type="number" value={fuelPct} onChange={(e) => setFuelPct(e.target.value)} step={0.5} /></div>
                    <div className="cb-field"><label>GST %</label><AutoComplete options={gstOpts} value={gstPct} onChange={setGstPct} /></div>
                    <div className="cb-field"><label>Insurance (₹)</label><input type="number" value={insAmt} onChange={(e) => setInsAmt(e.target.value)} placeholder="0" /></div>
                    <div className="cb-field"><label>Other Charges (₹)</label><input type="number" value={otherAmt} onChange={(e) => setOtherAmt(e.target.value)} placeholder="0" /></div>
                  </div>
                  <div className="cb-rate-box">
                    {[
                      ["Freight Charge", rate.freight],
                      ["Fuel Surcharge",  rate.fuel],
                      ["Insurance",       rate.ia],
                      ["Other Charges",   rate.oa],
                      ["Sub Total",       rate.sub],
                      [`GST (${gstPct}%)`, rate.gst],
                    ].map(([lbl, val]) => (
                      <div key={lbl} className="cb-r-row">
                        <span className="cb-r-lbl">{lbl}</span>
                        <span className="cb-r-val">₹ {fmt(val)}</span>
                      </div>
                    ))}
                    <div className="cb-r-row cb-r-total">
                      <span className="cb-r-lbl">Total Payable</span>
                      <span className="cb-r-val">₹ {fmt(rate.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="cb-actions">
              <button className="cb-btn cb-btn--ghost" onClick={() => setStep(2)}>← Back</button>
              <button className="cb-btn cb-btn--teal cb-btn--lg" onClick={openConfirm}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Confirm &amp; Book
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ════════ CONFIRM MODAL ════════ */}
      {confirmOpen && (
        <div className="cb-overlay show">
          <div className="cb-modal">
            <div className="cb-modal-hd">
              <h3>Confirm Booking</h3>
              <button className="cb-modal-x" onClick={() => setConfirmOpen(false)}>✕</button>
            </div>
            <div className="cb-modal-body">
              <p style={{ fontSize: 12.5, color: "var(--slate)", lineHeight: 1.6 }}>
                Review details before finalising. CNR and barcode sticker will be generated on confirmation.
              </p>
              <div className="cb-m-details">
                {[
                  ["CNR Number",     cnr],
                  ["Route",          `${bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || PLACE_NAMES[bookingPlace] || "—"} → ${deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || PLACE_NAMES[deliveryPlace] || "—"}`],
                  ["Party",          partyOpts.find(o => String(o.value) === String(partyKey))?.label || PARTIES[partyKey]?.name || "—"],
                  ["Pieces",         tQ + " pcs"],
                  ["Charged Weight", `Act: ${tA.toFixed(2)} kg | Chg: ${tC.toFixed(2)} kg`],
                  ["Images",         images.length ? images.length + " file(s)" : "None"],
                  ["Expected Del.",  formatDateDisplay(expDelivery)],
                  ["OTP Delivery",   otpOn ? `Enabled — ${otpMobile || "No mobile set"}` : "Not required"],
                  ["Notifications",  [smsOn && "SMS", emailOn && "Email", waOn && "WhatsApp"].filter(Boolean).join(" + ") || "None"],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="cb-m-row">
                    <span className="ml">{lbl}</span>
                    <span className="mv" style={lbl === "CNR Number" ? { color: "var(--navy)", fontSize: 14, letterSpacing: 1 } : {}}>{val}</span>
                  </div>
                ))}
                <div className="cb-m-row" style={{ borderTop: "2px solid var(--border)", marginTop: 4, paddingTop: 8 }}>
                  <span className="ml" style={{ fontWeight: 700, color: "var(--teal)", fontSize: 13.5 }}>Total Payable</span>
                  <span className="mv" style={{ fontSize: 18, color: "var(--teal)" }}>₹ {fmt(rate.total)}</span>
                </div>
              </div>
            </div>
            <div className="cb-modal-ft">
              <button className="cb-btn cb-btn--ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button className="cb-btn cb-btn--teal" onClick={doBook} disabled={isBooking}>
                {isBooking ? "Saving..." : "✓ Confirm & Book"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ SUCCESS MODAL ════════ */}
      {successOpen && (
        <div className="cb-overlay show">
          <div className="cb-modal" style={{ maxWidth: 440, textAlign: "center" }}>
            <div style={{ padding: "32px 28px 24px" }}>
              {/* Checkmark circle */}
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "linear-gradient(135deg, #0D9488, #0F766E)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px", boxShadow: "0 8px 24px rgba(13,148,136,0.35)"
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{ width: 38, height: 38 }}>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800, color: "var(--navy, #1A2940)" }}>
                Booking Confirmed!
              </h2>
              <p style={{ margin: "0 0 22px", fontSize: 13, color: "var(--slate, #6B7A99)" }}>
                Your consignment has been booked successfully.
              </p>

              {/* CNR highlight box */}
              <div style={{
                background: "linear-gradient(135deg, #EFF9F8, #D0F2EF)",
                border: "2px solid #0D9488",
                borderRadius: 12, padding: "16px 20px", marginBottom: 22
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
                  textTransform: "uppercase", color: "#0F766E", marginBottom: 6 }}>
                  Consignment Number
                </div>
                <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 3,
                  color: "var(--navy, #1A2940)", fontFamily: "monospace" }}>
                  {bookedCnr}
                </div>
              </div>

              {/* Quick summary */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr",
                gap: 10, marginBottom: 24, textAlign: "left"
              }}>
                {[
                  ["From", stickerData.from],
                  ["To",   stickerData.to],
                  ["Consignor", stickerData.cnor],
                  ["Consignee", stickerData.cnee],
                  ["Pieces",    stickerData.pcs],
                  ["Amount",    stickerData.amt],
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{
                    background: "#F7F9FC", borderRadius: 8, padding: "9px 12px"
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: 0.8, color: "#8AA5C0", marginBottom: 3 }}>{lbl}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700,
                      color: "var(--navy, #1A2940)", wordBreak: "break-word" }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button className="cb-btn cb-btn--ghost" style={{ flex: 1 }}
                  onClick={() => { setSuccessOpen(false); newBooking(); }}>
                  + New Booking
                </button>
                <button className="cb-btn cb-btn--teal" style={{ flex: 1 }}
                  onClick={() => { setSuccessOpen(false); setStickerOpen(true); }}>
                  🖨 View Sticker
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ STICKER OVERLAY ════════ */}
      {stickerOpen && (
        <div className="cb-sticker-overlay show">
          <div className="cb-sticker-card">
            <div className="cb-sticker-hd">
              <div>
                <h3>Consignment Sticker Ready</h3>
                <p>Print and affix to every package before dispatch</p>
              </div>
              <button className="cb-sticker-x" onClick={() => setStickerOpen(false)}>✕</button>
            </div>
            <div className="cb-sticker-body">
              <div className="cb-sticker-print">
                <div id="cb-sticker-print-inner">
                  <div className="cb-sl-logo">
                    <div className="cb-sl-mark">
                      <svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" /></svg>
                    </div>
                    <div className="cb-sl-name">Saami Tradestar Logistics Pvt Ltd</div>
                  </div>
                  <div className="cb-s-cnr">{stickerData.cnr}</div>
                  <svg ref={barcodeSvgRef} style={{ width: "100%" }}></svg>
                  <div className="cb-s-route">
                    <span>{stickerData.from}</span>
                    <div className="arr" />
                    <span>{stickerData.to}</span>
                  </div>
                  <div className="cb-s-info">
                    {[
                      ["Consignor",  stickerData.cnor],
                      ["Consignee",  stickerData.cnee],
                      ["Pieces",     stickerData.pcs],
                      ["Chg Weight", stickerData.wt],
                      ["Date",       stickerData.date],
                      ["Amount",     stickerData.amt],
                    ].map(([l, v]) => (
                      <div key={l} className="cb-s-cell">
                        <div className="cb-s-cell-l">{l}</div>
                        <div className="cb-s-cell-v" style={l === "Amount" ? { color: "var(--teal)" } : {}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="cb-sticker-acts">
                <button className="cb-btn cb-btn--ghost" style={{ flex: 1 }} onClick={() => setStickerOpen(false)}>Close</button>
                <button className="cb-btn cb-btn--amber" style={{ flex: 1 }} onClick={printSticker}>🖨 Print</button>
                <button className="cb-btn cb-btn--primary" style={{ flex: 1 }} onClick={newBooking}>+ New Booking</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TOASTS ── */}
      <ToastContainer toasts={toasts} />
    </div>
  );
}